// Angular
import { HttpClient } from '@angular/common/http';
import { inject, Service } from '@angular/core';

// RxJS
import { exhaustMap, Observable, switchMap, take } from 'rxjs';

// Data Contracts
import {
  JudgeChamberAssignmentRequest,
  JudgeChamberClerkReviewDocumentNotes,
  JudgeChamberClerkReviewNotesQuery,
  JudgeChamberDocumentAssignment,
  JudgeChamberFilingDocumentAssignment,
  JudgeChamberMentionCandidate,
  JudgeChamberNotificationList,
  JudgeChamberNotificationReadRequest,
  JudgeReviewSavedFilter,
  JudgeReviewSavedFilterRequest,
  JudgeChamberFilingDocumentTags,
  JudgeChamberNote,
  JudgeChamberNoteRequest,
  JudgeChambersSummary,
  JudgeChamberTag,
  JudgeChamberTargetNotes,
} from '@data-contracts';

// Local
import { ENV_CONFIG } from '@app/core/app-config/app-config.service';
import {
  IJudgeChambersEndpoints,
  judgeChambersEndpoints,
  withTrailingSlash,
} from '@app/types/api/endpoints/judge-review.endpoints';
import { EnvConfig } from '@app/types/environments';

/**
 * The Judge Chambers API endpoints.
 */
@Service()
export class JudgeChambersApiService {
  /**
   * The Angular HttpClient service used to make the API calls.
   */
  private readonly http = inject(HttpClient);

  /**
   * Config gate for every request. `ENV_CONFIG` never completes, so `take(1)`
   * closes the gate after the first value and each request completes with its
   * response.
   */
  private readonly envConfig = inject<Observable<EnvConfig>>(ENV_CONFIG).pipe(
    take(1),
  );

  /** The Judge Chambers endpoint URLs for the configured API server. */
  private urls(envConfig: EnvConfig): IJudgeChambersEndpoints {
    return judgeChambersEndpoints(
      withTrailingSlash(envConfig.ApiServer.BaseURL),
    );
  }

  /**
   * Gets the judge chambers the user is the judge of or an active member of,
   * with chamber tags, tags on pending documents, and the user's unread
   * notification count.
   *
   * @returns The judge chambers summary.
   */
  getJudgeChambersSummary(): Observable<JudgeChambersSummary> {
    return this.envConfig.pipe(
      switchMap((envConfig: EnvConfig) =>
        this.http.get<JudgeChambersSummary>(this.urls(envConfig).summary()),
      ),
    );
  }

  /**
   * Creates a chamber tag.
   *
   * @param chamberId The chamber id.
   * @param name The tag name.
   * @returns The chamber's tags, sorted by name.
   */
  createTag(chamberId: string, name: string): Observable<JudgeChamberTag[]> {
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) =>
        this.http.post<JudgeChamberTag[]>(
          this.urls(envConfig).tags(chamberId),
          { name },
        ),
      ),
    );
  }

  /**
   * Renames a chamber tag.
   *
   * @param chamberId The chamber id.
   * @param tagId The tag id.
   * @param name The new tag name.
   * @returns The chamber's tags, sorted by name.
   */
  renameTag(
    chamberId: string,
    tagId: string,
    name: string,
  ): Observable<JudgeChamberTag[]> {
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) =>
        this.http.put<JudgeChamberTag[]>(
          this.urls(envConfig).tag(chamberId, tagId),
          { name },
        ),
      ),
    );
  }

  /**
   * Deletes a chamber tag that is not applied to any document.
   *
   * @param chamberId The chamber id.
   * @param tagId The tag id.
   * @returns The chamber's remaining tags, sorted by name.
   */
  deleteTag(chamberId: string, tagId: string): Observable<JudgeChamberTag[]> {
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) =>
        this.http.delete<JudgeChamberTag[]>(
          this.urls(envConfig).tag(chamberId, tagId),
        ),
      ),
    );
  }

  /**
   * Applies a chamber tag to a document pending the chamber judge's review.
   *
   * @param chamberId The chamber id.
   * @param documentId The document id.
   * @param tagId The tag id.
   * @returns The chamber tags on the document, sorted by name.
   */
  applyDocumentTag(
    chamberId: string,
    documentId: number,
    tagId: string,
  ): Observable<JudgeChamberTag[]> {
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) =>
        this.http.put<JudgeChamberTag[]>(
          this.urls(envConfig).documentTag(chamberId, documentId, tagId),
          null,
        ),
      ),
    );
  }

  /**
   * Removes a chamber tag from a document.
   *
   * @param chamberId The chamber id.
   * @param documentId The document id.
   * @param tagId The tag id.
   * @returns The chamber tags left on the document, sorted by name.
   */
  removeDocumentTag(
    chamberId: string,
    documentId: number,
    tagId: string,
  ): Observable<JudgeChamberTag[]> {
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) =>
        this.http.delete<JudgeChamberTag[]>(
          this.urls(envConfig).documentTag(chamberId, documentId, tagId),
        ),
      ),
    );
  }

  /**
   * Gets the chamber tags for each document of a filing pending review by the
   * judge of a chamber the user works in. Empty for courts without judge
   * chambers.
   *
   * @param filingUniqueId The filing unique id.
   * @returns The per-document chamber tags.
   */
  getFilingDocumentTags(
    filingUniqueId: string,
  ): Observable<JudgeChamberFilingDocumentTags[]> {
    return this.envConfig.pipe(
      switchMap((envConfig: EnvConfig) =>
        this.http.get<JudgeChamberFilingDocumentTags[]>(
          this.urls(envConfig).filingDocumentTags(filingUniqueId),
        ),
      ),
    );
  }

  /**
   * Gets a chamber's notes on a document, newest first.
   */
  getDocumentNotes(
    chamberId: string,
    documentId: number,
  ): Observable<JudgeChamberNote[]> {
    return this.envConfig.pipe(
      switchMap((envConfig: EnvConfig) =>
        this.http.get<JudgeChamberNote[]>(
          this.urls(envConfig).documentNotes(chamberId, documentId),
        ),
      ),
    );
  }

  /**
   * Adds a chamber note to a document.
   *
   * @returns The chamber's notes on the document, newest first.
   */
  addDocumentNote(
    chamberId: string,
    documentId: number,
    request: JudgeChamberNoteRequest,
  ): Observable<JudgeChamberNote[]> {
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) =>
        this.http.post<JudgeChamberNote[]>(
          this.urls(envConfig).documentNotes(chamberId, documentId),
          request,
        ),
      ),
    );
  }

  /**
   * Gets a chamber's notes on a case, newest first.
   */
  getCaseNotes(
    chamberId: string,
    caseId: number,
  ): Observable<JudgeChamberNote[]> {
    return this.envConfig.pipe(
      switchMap((envConfig: EnvConfig) =>
        this.http.get<JudgeChamberNote[]>(
          this.urls(envConfig).caseNotes(chamberId, caseId),
        ),
      ),
    );
  }

  /**
   * Adds a chamber note to a case.
   *
   * @returns The chamber's notes on the case, newest first.
   */
  addCaseNote(
    chamberId: string,
    caseId: number,
    request: JudgeChamberNoteRequest,
  ): Observable<JudgeChamberNote[]> {
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) =>
        this.http.post<JudgeChamberNote[]>(
          this.urls(envConfig).caseNotes(chamberId, caseId),
          request,
        ),
      ),
    );
  }

  /**
   * Edits a chamber note (author only).
   *
   * @returns The chamber's notes on the same document or case.
   */
  updateNote(
    chamberId: string,
    noteId: string,
    request: JudgeChamberNoteRequest,
  ): Observable<JudgeChamberNote[]> {
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) =>
        this.http.put<JudgeChamberNote[]>(
          this.urls(envConfig).note(chamberId, noteId),
          request,
        ),
      ),
    );
  }

  /**
   * Deletes a chamber note (author only).
   *
   * @returns The chamber's remaining notes on the same document or case.
   */
  deleteNote(
    chamberId: string,
    noteId: string,
  ): Observable<JudgeChamberNote[]> {
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) =>
        this.http.delete<JudgeChamberNote[]>(
          this.urls(envConfig).note(chamberId, noteId),
        ),
      ),
    );
  }

  /**
   * Gets the chamber notes for each pending document and each case of a filing.
   * Empty for courts without judge chambers.
   */
  getFilingNotes(
    filingUniqueId: string,
  ): Observable<JudgeChamberTargetNotes[]> {
    return this.envConfig.pipe(
      switchMap((envConfig: EnvConfig) =>
        this.http.get<JudgeChamberTargetNotes[]>(
          this.urls(envConfig).filingNotes(filingUniqueId),
        ),
      ),
    );
  }

  /**
   * Replies to a chamber note (one level deep).
   *
   * @returns The chamber's notes on the note's document or case, with replies.
   */
  addReply(
    chamberId: string,
    noteId: string,
    note: string,
  ): Observable<JudgeChamberNote[]> {
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) =>
        this.http.post<JudgeChamberNote[]>(
          this.urls(envConfig).noteReplies(chamberId, noteId),
          { note } satisfies JudgeChamberNoteRequest,
        ),
      ),
    );
  }

  /**
   * Gets the people who can be mentioned ("@First Last") in a chamber's notes:
   * its members, then the clerks who review the court's filings (who can only
   * be mentioned in document notes shown in clerk review).
   */
  getMentionCandidates(
    chamberId: string,
  ): Observable<JudgeChamberMentionCandidate[]> {
    return this.envConfig.pipe(
      switchMap((envConfig: EnvConfig) =>
        this.http.get<JudgeChamberMentionCandidate[]>(
          this.urls(envConfig).mentionCandidates(chamberId),
        ),
      ),
    );
  }

  /**
   * Gets the people who can be mentioned in a note on a document in clerk
   * review: the document's chamber, and the clerks who review the court's
   * filings.
   */
  getClerkReviewMentionCandidates(
    documentId: number,
  ): Observable<JudgeChamberMentionCandidate[]> {
    return this.envConfig.pipe(
      switchMap((envConfig: EnvConfig) =>
        this.http.get<JudgeChamberMentionCandidate[]>(
          this.urls(envConfig).clerkReviewMentionCandidates(documentId),
        ),
      ),
    );
  }

  /**
   * Gets the chamber notes shown in clerk review on documents of the clerk
   * review list. Documents the user cannot review, or in courts without judge
   * chambers, are left out.
   */
  getClerkReviewDocumentNotes(
    documentIds: number[],
  ): Observable<JudgeChamberClerkReviewDocumentNotes[]> {
    return this.envConfig.pipe(
      switchMap((envConfig: EnvConfig) =>
        this.http.post<JudgeChamberClerkReviewDocumentNotes[]>(
          this.urls(envConfig).clerkReviewDocumentNotesQuery(),
          { documentIds } satisfies JudgeChamberClerkReviewNotesQuery,
        ),
      ),
    );
  }

  /**
   * Gets the chamber notes shown in clerk review on a document, newest first.
   */
  getClerkReviewNotes(documentId: number): Observable<JudgeChamberNote[]> {
    return this.envConfig.pipe(
      switchMap((envConfig: EnvConfig) =>
        this.http.get<JudgeChamberNote[]>(
          this.urls(envConfig).clerkReviewDocumentNotes(documentId),
        ),
      ),
    );
  }

  /**
   * Adds a note to a document from clerk review; it is always shown in clerk
   * review.
   *
   * @returns The notes shown in clerk review on the document, newest first.
   */
  addClerkReviewNote(
    documentId: number,
    note: string,
  ): Observable<JudgeChamberNote[]> {
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) =>
        this.http.post<JudgeChamberNote[]>(
          this.urls(envConfig).clerkReviewDocumentNotes(documentId),
          {
            note,
            availableInClerkReview: true,
          } satisfies JudgeChamberNoteRequest,
        ),
      ),
    );
  }

  /**
   * Edits a note written in clerk review (author only).
   *
   * @returns The notes shown in clerk review on the note's document.
   */
  updateClerkReviewNote(
    noteId: string,
    note: string,
  ): Observable<JudgeChamberNote[]> {
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) =>
        this.http.put<JudgeChamberNote[]>(
          this.urls(envConfig).clerkReviewNote(noteId),
          {
            note,
            availableInClerkReview: true,
          } satisfies JudgeChamberNoteRequest,
        ),
      ),
    );
  }

  /**
   * Deletes a note written in clerk review, and its replies (author only).
   *
   * @returns The remaining notes shown in clerk review on the note's document.
   */
  deleteClerkReviewNote(noteId: string): Observable<JudgeChamberNote[]> {
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) =>
        this.http.delete<JudgeChamberNote[]>(
          this.urls(envConfig).clerkReviewNote(noteId),
        ),
      ),
    );
  }

  /**
   * Replies from clerk review to a note shown in clerk review.
   *
   * @returns The notes shown in clerk review on the note's document.
   */
  addClerkReviewReply(
    noteId: string,
    note: string,
  ): Observable<JudgeChamberNote[]> {
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) =>
        this.http.post<JudgeChamberNote[]>(
          this.urls(envConfig).clerkReviewNoteReplies(noteId),
          { note } satisfies JudgeChamberNoteRequest,
        ),
      ),
    );
  }

  /**
   * Assigns a pending document to an active chamber member, or unassigns it
   * when `userUniqueId` is null.
   *
   * @returns The document's assignment.
   */
  assignDocument(
    chamberId: string,
    documentId: number,
    userUniqueId: string | null,
  ): Observable<JudgeChamberDocumentAssignment> {
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) =>
        this.http.put<JudgeChamberDocumentAssignment>(
          this.urls(envConfig).documentAssignee(chamberId, documentId),
          { userUniqueId } satisfies JudgeChamberAssignmentRequest,
        ),
      ),
    );
  }

  /**
   * The filing's documents pending review by the judge of a chamber the user
   * works in, with their assignee, the chamber's members, and whether the user
   * may act on each. Empty for courts without judge chambers.
   */
  getFilingAssignments(
    filingUniqueId: string,
  ): Observable<JudgeChamberFilingDocumentAssignment[]> {
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) =>
        this.http.get<JudgeChamberFilingDocumentAssignment[]>(
          this.urls(envConfig).filingAssignments(filingUniqueId),
        ),
      ),
    );
  }

  /** The user's chamber notifications, newest first, with the unread count. */
  getNotifications(
    unreadOnly: boolean,
  ): Observable<JudgeChamberNotificationList> {
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) =>
        this.http.get<JudgeChamberNotificationList>(
          this.urls(envConfig).notifications(),
          { params: { unreadOnly } },
        ),
      ),
    );
  }

  /** Marks one of the user's notifications read or unread. */
  setNotificationRead(
    notificationId: string,
    isRead: boolean,
  ): Observable<void> {
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) =>
        this.http.put<void>(
          this.urls(envConfig).notificationRead(notificationId),
          { isRead } satisfies JudgeChamberNotificationReadRequest,
        ),
      ),
    );
  }

  /** Marks all of the user's notifications read. */
  markAllNotificationsRead(): Observable<void> {
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) =>
        this.http.put<void>(this.urls(envConfig).notificationsRead(), null),
      ),
    );
  }

  /** Deletes one of the user's notifications. */
  deleteNotification(notificationId: string): Observable<void> {
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) =>
        this.http.delete<void>(
          this.urls(envConfig).notification(notificationId),
        ),
      ),
    );
  }

  /** The user's saved Judge Review list filters, by name (private to the user). */
  getSavedFilters(): Observable<JudgeReviewSavedFilter[]> {
    return this.envConfig.pipe(
      switchMap((envConfig: EnvConfig) =>
        this.http.get<JudgeReviewSavedFilter[]>(
          this.urls(envConfig).savedFilters(),
        ),
      ),
    );
  }

  /** Saves a Judge Review list filter under a name; returns the user's saved filters. */
  createSavedFilter(
    request: JudgeReviewSavedFilterRequest,
  ): Observable<JudgeReviewSavedFilter[]> {
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) =>
        this.http.post<JudgeReviewSavedFilter[]>(
          this.urls(envConfig).savedFilters(),
          request,
        ),
      ),
    );
  }

  /** Deletes one of the user's saved filters; returns the user's saved filters. */
  deleteSavedFilter(
    savedFilterId: string,
  ): Observable<JudgeReviewSavedFilter[]> {
    return this.envConfig.pipe(
      exhaustMap((envConfig: EnvConfig) =>
        this.http.delete<JudgeReviewSavedFilter[]>(
          this.urls(envConfig).savedFilter(savedFilterId),
        ),
      ),
    );
  }
}
